import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import Login from './pages/Login';
import AdminDashboard from './components/Admin/AdminDashboard';
import TeacherDashboard from './components/Teacher/TeacherDashboard';

const AUTH_KEYS = [
  'currentUser',
  'userRole',
  'isAdminLoggedIn',
  'isTeacherLoggedIn',
  'isStudentLoggedIn',
  'adminId',
  'adminName',
  'teacherId',
  'teacherName',
  'studentId',
  'studentName'
];

const clearAuthStorage = () => {
  AUTH_KEYS.forEach((key) => sessionStorage.removeItem(key));
};

const normalizeRole = (role) => {
  if (role === 'lab') return 'teacher';
  return role;
};

const isValidUser = (user) => {
  if (!user || typeof user !== 'object') return false;
  if (!user.role) return false;

  const role = normalizeRole(user.role);

  if (!['admin', 'teacher'].includes(role)) return false;

  return true;
};

const ProtectedRoute = ({ children, user, requiredRole }) => {
  if (!isValidUser(user)) {
    return <Navigate to="/login" replace />;
  }

  const userRole = normalizeRole(user.role);
  const neededRole = normalizeRole(requiredRole);

  if (neededRole && userRole !== neededRole) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedUser = sessionStorage.getItem('currentUser');

    if (!savedUser) {
      clearAuthStorage();
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const parsedUser = JSON.parse(savedUser);

      if (!isValidUser(parsedUser)) {
        clearAuthStorage();
        setUser(null);
        setLoading(false);
        return;
      }

      setUser(parsedUser);
      sessionStorage.setItem('userRole', parsedUser.role);
    } catch (error) {
      console.error('JSON парсинг катасы:', error);
      clearAuthStorage();
      setUser(null);
    }

    setLoading(false);
  }, []);

  const handleLogin = (userData) => {
    if (!isValidUser(userData)) {
      clearAuthStorage();
      setUser(null);
      return;
    }

    setUser(userData);

    sessionStorage.setItem('currentUser', JSON.stringify(userData));
    sessionStorage.setItem('userRole', userData.role);

    sessionStorage.removeItem('isAdminLoggedIn');
    sessionStorage.removeItem('isTeacherLoggedIn');
    sessionStorage.removeItem('isStudentLoggedIn');

    if (userData.role === 'admin') {
      sessionStorage.setItem('isAdminLoggedIn', 'true');
      sessionStorage.setItem('adminId', userData.id || userData.uid || '');
      sessionStorage.setItem('adminName', userData.fullName || userData.name || '');
    }

    if (userData.role === 'teacher' || userData.role === 'lab') {
      sessionStorage.setItem('isTeacherLoggedIn', 'true');
      sessionStorage.setItem('teacherId', userData.id || userData.uid || '');
      sessionStorage.setItem('teacherName', userData.fullName || userData.name || '');
    }
  };

  const handleLogout = () => {
    setUser(null);
    clearAuthStorage();
  };

  if (loading) {
    return <div className="app-loading">Загрузка...</div>;
  }

  return (
    <Router>
      <div className="app-main">
        <Routes>
          <Route
            path="/login"
            element={<Login onLogin={handleLogin} onLogout={handleLogout} />}
          />

          <Route
            path="/admin"
            element={
              <ProtectedRoute user={user} requiredRole="admin">
                <AdminDashboard onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/teacher"
            element={
              <ProtectedRoute user={user} requiredRole="teacher">
                <TeacherDashboard onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/"
            element={
              isValidUser(user) ? (
                <Navigate
                  to={normalizeRole(user.role) === 'admin' ? '/admin' : '/teacher'}
                  replace
                />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;